import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Ticket } from "lucide-react";
import { toast } from "sonner";

export const WelcomePassAdmin = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/welcome-passes").then((r) => setRows(r.data.passes)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  const pending = rows.filter((r) => r.status === "unused" && !r.announced_at).length;
  const announce = async () => {
    if (!window.confirm(`Send the Welcome Day Pass to ${pending} explorer${pending === 1 ? "" : "s"}' families (in-app message + email)?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/welcome-passes/announce"); toast.success(`Sent to ${data.families} famil${data.families === 1 ? "y" : "ies"} (${data.emailed} emailed)`); load(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const remindable = rows.filter((r) => r.status === "unused" && !r.expired).length;
  const remind = async () => {
    if (!window.confirm(`Send a "last chance" in-app reminder to the families of ${remindable} explorer${remindable === 1 ? "" : "s"} who haven't used their pass?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/welcome-passes/remind"); toast.success(`Reminder sent to ${data.families} famil${data.families === 1 ? "y" : "ies"} in the app`); load(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-2xl border border-gold/40 p-4 space-y-3" data-testid="welcome-pass-admin">
      <div className="flex flex-wrap items-center gap-2">
        <Ticket className="w-5 h-5 text-coral" />
        <p className="font-display font-bold text-navy">FREE Welcome Day Pass ({rows.length} explorers · {rows.filter((r) => r.status === "redeemed").length} used)</p>
        <Button size="sm" disabled={busy || !pending} onClick={announce} className="ml-auto rounded-full bg-gold text-navy font-bold hover:bg-gold/90" data-testid="welcome-pass-announce">Send pass to {pending} new</Button>
        <Button size="sm" disabled={busy || !remindable} onClick={remind} className="rounded-full bg-coral text-white font-bold" data-testid="welcome-pass-remind">Remind {remindable} unused</Button>
      </div>
      <p className="text-xs text-slate-500">One free visit per Prospect Preschool explorer with an after-school booking. Valid Saturdays & Sundays, 10 AM – 6 PM, until Sat Oct 17. Staff redeem the code on the Register. New sign-ups get a pass automatically on their dashboard. Families with an unused pass get an automatic in-app + email reminder 3 days before it expires.</p>
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap justify-between gap-2 py-2 text-sm" data-testid={`welcome-pass-row-${r.id}`}>
            <span className="text-navy font-semibold">{r.child_name} <span className="text-xs text-slate-400 font-normal">{r.parent_name}</span></span>
            <span className="flex items-center gap-2 text-xs">
              <span className="font-mono font-bold text-navy">{r.code}</span>
              <span className={`rounded-full px-2 py-0.5 font-bold ${r.status === "redeemed" ? "bg-emerald-100 text-emerald-700" : r.expired ? "bg-slate-100 text-slate-500" : r.announced_at ? "bg-turquoise/15 text-turquoise" : "bg-amber-100 text-amber-700"}`}>
                {r.status === "redeemed" ? `used ${r.redeemed_date}` : r.expired ? "expired" : r.reminded_at ? "reminded" : r.announced_at ? "sent" : "not sent"}
              </span>
            </span>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-400 py-3">No eligible Prospect explorers yet.</p>}
      </div>
    </div>
  );
};
